import type { ReactNode } from 'react';

/** A selectable pill built from tokens. Inline styles, because the dashboards
 *  button reset outranks single utility classes. Use `role="radio"` inside a
 *  radio group, or the default pressed-button semantics for independent toggles. */
export function TogglePill({
  selected,
  onClick,
  role,
  tabIndex,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  role?: 'radio';
  /** Roving tab stop inside a radio group. */
  tabIndex?: number;
  children: ReactNode;
}) {
  const semantics =
    role === 'radio' ? { role, 'aria-checked': selected } : { 'aria-pressed': selected };
  return (
    <button
      type="button"
      {...semantics}
      tabIndex={tabIndex}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 28,
        padding: '0 12px',
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 700,
        cursor: 'pointer',
        background: selected ? 'var(--nb-primary-soft)' : 'var(--nb-bg)',
        color: selected ? 'var(--nb-primary)' : 'var(--nb-muted)',
        border: `1px solid ${selected ? 'var(--nb-primary)' : 'var(--nb-border-2)'}`,
      }}
    >
      {selected && (
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden>
          <path
            d="M2 6.5l2.5 2.5L10 3.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {children}
    </button>
  );
}
